import { apiClient } from './client';
import type { components } from './schema.gen';

export type User = components['schemas']['UserView'];

/**
 * Rôles plateforme. ADMIN / OPERATOR / VIEWER ouvrent le back-office ;
 * HEATMAP n'ouvre que la carte (front PDE.CI Heatmap) — un compte qui ne porte
 * que lui ne voit aucune fiche.
 */
export type PlatformRole = 'ADMIN' | 'OPERATOR' | 'VIEWER' | 'HEATMAP';

export const ROLE_LABELS: Record<PlatformRole, { label: string; help: string }> = {
  ADMIN: { label: 'Administrateur', help: 'Configuration, comptes, arbitrages, audit — et tout le reste.' },
  OPERATOR: { label: 'Opérateur', help: 'Imports, déclaration d’événements, conflits, fiches.' },
  VIEWER: { label: 'Lecteur', help: 'Consultation seule des fiches.' },
  HEATMAP: { label: 'Accès heatmap', help: 'Ouvre la carte. N’ouvre rien du back-office.' },
};

export type UserFilters = {
  q?: string;
  source?: string;
  role?: PlatformRole | '';
  includeDisabled?: boolean;
  limit?: number;
  offset?: number;
};

export type UserListResult = { items: User[]; total: number; roles: PlatformRole[] };

/** Le mot de passe n'accompagne la réponse qu'à la création ou après une réinitialisation. */
export type UserResult = { user: User; password?: string };

function fail(error: unknown, status: number, fallback: string): never {
  const e = error as { detail?: string; title?: string } | undefined;
  throw new Error(e?.detail ?? e?.title ?? `${fallback} (${status})`);
}

export async function listUsers(filters: UserFilters = {}): Promise<UserListResult> {
  const { data, error, response } = await apiClient.GET('/v1/admin/users', {
    params: {
      query: {
        q: filters.q ?? '',
        source: filters.source ?? '',
        role: (filters.role ?? '') as never,
        includeDisabled: filters.includeDisabled ?? false,
        limit: filters.limit ?? 100,
        offset: filters.offset ?? 0,
      },
    },
  });
  if (error || !data) fail(error, response.status, 'Lecture des comptes impossible');
  return {
    items: data.items ?? [],
    total: data.total ?? 0,
    roles: (data.roles ?? []) as PlatformRole[],
  };
}

export async function createUser(input: {
  email: string;
  displayName?: string;
  roles: PlatformRole[];
  password?: string;
}): Promise<UserResult> {
  const { data, error, response } = await apiClient.POST('/v1/admin/users', {
    body: {
      email: input.email,
      displayName: input.displayName || undefined,
      roles: input.roles,
      password: input.password || undefined,
    },
  });
  if (error || !data) fail(error, response.status, 'Création du compte impossible');
  return data as UserResult;
}

export async function updateUser(
  id: number,
  input: { roles?: PlatformRole[]; displayName?: string },
): Promise<UserResult> {
  const { data, error, response } = await apiClient.PATCH('/v1/admin/users/{id}', {
    params: { path: { id } },
    body: { roles: input.roles, displayName: input.displayName },
  });
  if (error || !data) fail(error, response.status, 'Modification du compte impossible');
  return data as UserResult;
}

export async function resetUserPassword(id: number, password?: string): Promise<UserResult> {
  const { data, error, response } = await apiClient.POST('/v1/admin/users/{id}/password', {
    params: { path: { id } },
    body: { password: password || undefined },
  });
  if (error || !data) fail(error, response.status, 'Réinitialisation impossible');
  return data as UserResult;
}

export async function setUserDisabled(
  id: number,
  disabled: boolean,
  reason?: string,
): Promise<UserResult> {
  const { data, error, response } = await apiClient.POST('/v1/admin/users/{id}/status', {
    params: { path: { id } },
    body: { disabled, reason: reason || undefined },
  });
  if (error || !data) fail(error, response.status, 'Changement d’état impossible');
  return data as UserResult;
}
